import { Box, Typography } from '@mui/material';
import type { LandingPageBlock } from '@provision/landing-page-utils';
import { COPY } from '../copy';
import { resolveCta } from '../cta';
import { imageFitSx } from '../image-fit';
import { PageSurface } from '../layouts/block-surface';
import { useSurface } from '../layouts/block-surface-context';
import { useLayoutStyles } from '../layouts/layout-context';
import type { BlockRenderProps, HeroStyles } from '../layouts/types';
import { AccentText } from '../primitives/accent-text';
import { AnchorButton, CtaButton } from '../primitives/cta-button';
import { CheckIcon, PlayIcon } from '../primitives/icons';
import { Wrap } from '../primitives/wrap';

type HeroBlockData = Extract<LandingPageBlock, { type: 'hero' }>;

function renderMedia(block: HeroBlockData, s: HeroStyles) {
  if (block.mediaKind === 'image') {
    const url = block.image?.url?.trim();
    if (!url) return null;

    return (
      <Box sx={[s.mediaCard, s.mediaCardImage]}>
        <Box
          component="img"
          src={url}
          alt=""
          sx={[s.mediaImage, imageFitSx(block.imageFit, block.imageFocusX, block.imageFocusY)]}
        />
      </Box>
    );
  }

  if (block.mediaKind !== 'video') return null;

  const url = block.video?.mediaUrl?.trim();
  const linkProps = url
    ? { component: 'a' as const, href: url, target: '_blank', rel: 'noopener noreferrer' }
    : { component: 'div' as const };

  return (
    <Box {...linkProps} aria-label={COPY.playVideo} sx={s.mediaCard}>
      <Box sx={s.mediaScene} />
      <Box sx={s.play}>
        <PlayIcon />
      </Box>
      {block.video?.title || block.video?.caption ? (
        <Box sx={s.mediaMeta}>
          <div>
            {block.video.title ? <Box sx={s.mediaTitle}>{block.video.title}</Box> : null}
            {block.video.caption ? <Box sx={s.mediaCaption}>{block.video.caption}</Box> : null}
          </div>
        </Box>
      ) : null}
    </Box>
  );
}

export const HeroBlock = ({
  block,
  settings,
  plansBlockId,
  savingsPercent,
  onCta,
  pendingCta,
}: BlockRenderProps) => {
  const s = useLayoutStyles('hero');
  const secondaryVariant = useSurface('dark') === 'dark' ? 'dark' : 'ghost';
  if (block.type !== 'hero') return null;

  const media = renderMedia(block, s);
  const cta = resolveCta({ settings, onCta, pendingCta, blockId: block.id });

  return (
    <Box sx={s.section}>
      <Wrap>
        <Box sx={s.grid[media ? 'split' : 'single']}>
          <div>
            <Box sx={s.eyebrow}>{COPY.heroEyebrow}</Box>
            <Typography component="h1" sx={s.headline}>
              <AccentText text={block.headline} surface="dark" />
            </Typography>
            {block.subtext ? (
              <Typography component="p" sx={s.lede}>
                <AccentText text={block.subtext} surface="dark" />
              </Typography>
            ) : null}
            <Box sx={s.ctaRow}>
              <CtaButton target={cta} variant="brand" withArrow />
              {plansBlockId ? (
                <AnchorButton
                  href={`#${plansBlockId}`}
                  label={COPY.compareMemberships}
                  variant={secondaryVariant}
                />
              ) : null}
            </Box>
            {settings.showPricing && savingsPercent !== null ? (
              <Box sx={s.note}>
                <CheckIcon sx={s.noteIcon} />
                {COPY.savingsNote(savingsPercent)}
              </Box>
            ) : null}
          </div>
          {media ? (
            <PageSurface>
              <div>{media}</div>
            </PageSurface>
          ) : null}
        </Box>
      </Wrap>
    </Box>
  );
};
